import React from "react";

export default function GrowthLoopUserManual() {
  return (
    <div className="space-y-2 text-sm text-gray-100 leading-relaxed">
      <h1 className="text-lg lg:text-xl font-bold text-yellow-200">
        GrowthLoop – Complete User Manual
      </h1>

      <p className="text-white text-xs lg:text-sm">
        This manual explains how to use GrowthLoop as a
        <strong> continuous support system</strong>. GrowthLoop is not a test,
        diagnosis, or behaviour-control mechanism. It works through
        observation, structure, reflection, and consistency.
      </p>

      {/* PHILOSOPHY */}
      <section className="border border-cyan-500 rounded-xl p-2 bg-black">
        <h2 className="text-sm lg:text-lg font-semibold text-cyan-300">
          Core Principles
        </h2>
        <ul className="list-disc ml-5 space-y-1 [&_li]:text-white [&_li]:text-xs lg:[&_li]:text-white">
          <li>Support before labeling</li>
          <li>Structure before discipline</li>
          <li>Process before outcome</li>
          <li>Growth before judgment</li>
        </ul>
      </section>

      <div className="grid grid-cols-3 gap-1">
        <section className="border border-gray-300 rounded-xl p-2">
          <h2 className="text-sm lg:text-lg font-semibold text-cyan-300">
            Step 1: Child Setup
          </h2>
          <p className="text-white text-xs lg:text-sm">
            Enter the child’s name, age, and grade. This anchors all observations
            to developmental expectations rather than peer comparison.
          </p>
        </section>

        <section className="border border-gray-300 rounded-xl p-2">
          <h2 className="text-sm lg:text-lg font-semibold text-cyan-300">
            Step 2: Role-Based Assessments
          </h2>
          <p className="text-white text-xs lg:text-sm">
            Parents, educators, and doctors complete assessments independently.
            Each question is scored from 0–3 based on frequency or intensity.
          </p>
          <ul className="list-disc ml-5 space-y-1 [&_li]:text-white [&_li]:text-xs lg:[&_li]:text-white">
            <li>0 = Not observed</li>
            <li>1 = Occasionally</li>
            <li>2 = Frequently</li>
            <li>3 = Consistently</li>
          </ul>
          <p className="text-xs text-cyan-200 mt-2">
            Best practice: Answer based on the last 2–4 weeks.
          </p>
        </section>

        <section className="border border-gray-300 rounded-xl p-2">
          <h2 className="text-sm lg:text-lg font-semibold text-cyan-300">
            Step 3: Understanding Scores & Charts
          </h2>
          <p className="text-white text-xs lg:text-sm">
            Scores do not represent severity or diagnosis. They reflect
            concentration of observations within a domain.
          </p>
          <p className="text-white text-xs lg:text-sm">
            Higher scores indicate areas needing structural or environmental
            support—not correction.
          </p>
        </section>
      </div>

      <section className="border border-gray-300 rounded-xl p-2">
        <h2 className="text-sm lg:text-lg font-semibold text-cyan-300">
          Consistency Index Explained
        </h2>
        <p className="text-white text-xs lg:text-sm">
          The Consistency Index shows how aligned observations are across roles.
        </p>
        <ul className="list-disc ml-5 space-y-1 [&_li]:text-white [&_li]:text-xs lg:[&_li]:text-white">
          <li>High consistency → systemic pattern</li>
          <li>Low consistency → situational or environment-specific</li>
        </ul>
      </section>

      <div className="grid grid-cols-3 gap-1">
        <section className="border border-gray-300 rounded-xl p-2">
          <h2 className="text-sm lg:text-lg font-semibold text-cyan-300">
            DeepThinking (When & Why)
          </h2>
          <p className="text-white text-xs lg:text-sm">
            DeepThinking activates when scores or consistency suggest hidden
            drivers. These questions explore triggers, structure dependency,
            emotional load, and co-regulation needs.
          </p>
          <p className="text-xs text-cyan-200 mt-1">
            This is reflection, not interrogation.
          </p>
        </section>

        <section className="border border-gray-300 rounded-xl p-4">
          <h2 className="text-sm lg:text-lg font-semibold text-cyan-300">
            Chronicle – Daily Use
          </h2>
          <p className="text-white text-xs lg:text-sm">
            Chronicle captures attempts, routines, and follow-through. Even
            incomplete attempts matter.
          </p>
          <ul className="list-disc ml-5 space-y-1 [&_li]:text-white [&_li]:text-xs lg:[&_li]:text-white">
            <li>Log small routines</li>
            <li>Track continuity</li>
            <li>Observe effort trends</li>
          </ul>
        </section>

        <section className="border border-gray-300 rounded-xl p-4">
          <h2 className="text-sm lg:text-lg font-semibold text-cyan-300">
            Applause – Correct Usage
          </h2>
          <p className="text-white text-xs lg:text-sm">
            Applause is for effort, persistence, and completion—not talent or
            comparison.
          </p>
          <p className="text-xs text-cyan-200 mt-1">
            Avoid using Applause as reward bargaining.
          </p>
        </section>
      </div>

      <section className="border border-gray-300 rounded-xl p-2">
        <h2 className="text-sm lg:text-lg font-semibold text-cyan-300">
          Sharing & Reports
        </h2>
        <p className="text-white text-xs lg:text-sm">
          Reports are designed to support conversations with schools,
          counsellors, or doctors—not to label the child.
        </p>
      </section>

      {/* FINAL */}
      <p className="text-xs text-cyan-200 italic">
        GrowthLoop works when adults stay calm, consistent, and curious.
        Children grow when systems grow first.
      </p>
    </div>
  );
}
